import React from 'react';
import { Modal, Button, Descriptions, Card, Tag } from 'antd';
import { TruckOutlined } from '@ant-design/icons';
import countryList from 'react-select-country-list';
import { BRAND_PRIMARY } from '../../utils/brandColors';

const FreightDetailsModal = ({ 
  visible, 
  onCancel, 
  selectedFreight, 
  exchangeRates = {},
  zIndex = 1000 
}) => {
  // 获取国家列表数据
  const countries = countryList();
  const countryOptions = countries.getData();

  const getCountryName = (countryCode) => {
    const country = countryOptions.find(option => option.value === countryCode);
    return country ? country.label : countryCode;
  };

  const convertToUSD = (amount, currency) => {
    if (currency === 'USD') {
      return amount;
    }
    
    if (exchangeRates[currency]) {
      // rate_to_usd 表示 1 USD = X 目标货币，所以转换为USD需要除以这个值
      const usdRate = 1.0 / exchangeRates[currency];
      return amount * usdRate;
    }
    
    return amount; // 如果没有汇率信息，返回原值
  };

  const formatPriceWithUSD = (amount, currency) => {
    if (currency === 'USD') {
      return `$${parseFloat(amount).toFixed(2)}`;
    }
    
    const usdAmount = convertToUSD(parseFloat(amount), currency);
    return `${parseFloat(amount).toFixed(2)} ${currency} (~$${usdAmount.toFixed(2)})`;
  };

  const renderFreightDetails = (data) => {
    if (!data || typeof data !== 'object') return <div>No freight data available</div>;

    return (
      <div>
        {Object.entries(data).map(([countryCode, countryData]) => (
          <Card 
            key={countryCode} 
            size="small" 
            title={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Tag color={BRAND_PRIMARY} style={{ marginRight: 8 }}>{countryCode}</Tag>
                <strong>{getCountryName(countryCode)}</strong>
              </div>
            }
            style={{ marginBottom: 16 }}
            type="inner"
          >
            {countryData && typeof countryData === 'object' ? (
              <div>
                {/* ETA Information */}
                {countryData.eta && (
                  <div style={{ marginBottom: 12 }}>
                    <Tag color="green">ETA: {countryData.eta} days</Tag>
                  </div>
                )}
                
                {/* Weight Ranges */}
                {countryData.weight && typeof countryData.weight === 'object' ? (
                  <div>
                    <h5 style={{ marginBottom: 8, color: BRAND_PRIMARY }}>Weight Ranges & Pricing:</h5>
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {Object.entries(countryData.weight)
                        .sort(([a], [b]) => parseInt(a) - parseInt(b)) // Sort by weight
                        .map(([weight, pricing]) => (
                        <Card 
                          key={weight} 
                          size="small" 
                          style={{ backgroundColor: '#f8f9fa' }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <Tag color="orange">≤ {weight}g</Tag>
                                {pricing.currency && pricing.currency !== 'USD' && (
                                  <Tag color={BRAND_PRIMARY}>{pricing.currency}</Tag>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
                                {pricing.pre_packet !== undefined && (
                                  <span>
                                    <strong>Per Package:</strong> {formatPriceWithUSD(pricing.pre_packet || 0, pricing.currency || 'USD')}
                                  </span>
                                )}
                                {pricing.pre_kg !== undefined && (
                                  <span>
                                    <strong>Per KG:</strong> {formatPriceWithUSD(pricing.pre_kg || 0, pricing.currency || 'USD')}
                                  </span>
                                )}
                              </div>
                            </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ color: '#999', fontStyle: 'italic' }}>No weight pricing data available</div>
                )}
              </div>
            ) : (
              <div style={{ color: '#999', fontStyle: 'italic' }}>No pricing data available</div>
            )}
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <TruckOutlined style={{ marginRight: 8, color: BRAND_PRIMARY }} />
          Freight Channel Details: {selectedFreight?.name}
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button 
          key="close" 
          onClick={onCancel}
        >
          Close
        </Button>
      ]}
      width={800}
      zIndex={zIndex}
    >
      {selectedFreight && (
        <div>
          <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Channel ID" span={1}>
              {selectedFreight.id}
            </Descriptions.Item>
            <Descriptions.Item label="Channel Name" span={2}>
              {selectedFreight.name}
            </Descriptions.Item>
            <Descriptions.Item label="Created" span={1}>
              {new Date(selectedFreight.created_at).toLocaleString('en-US')}
            </Descriptions.Item>
            <Descriptions.Item label="Last Updated" span={2}>
              {new Date(selectedFreight.updated_at).toLocaleString('en-US')}
            </Descriptions.Item>
          </Descriptions>
          
          <h4 style={{ marginTop: 16, marginBottom: 12 }}>Freight Rates by Region:</h4>
          {renderFreightDetails(selectedFreight.data)}
        </div>
      )}
    </Modal>
  );
};

export default FreightDetailsModal;
